import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { buildApiUrl, getApiHeaders } from '@/config/api'

export interface UserProfile {
  id: number
  firstName: string | null
  lastName: string | null
  email: string
  phone: string | null
  avatar_url: string | null
  title: string | null
  location: string | null
  bio: string | null
  role: string
  department: string | null
  company: string | null
  is_recruiter: boolean
  recruiter_status?: 'pending_review' | 'approved' | 'hold' | null
  is_jobseeker: boolean
  createdAt: string
  lastLogin: string | null
  subscription: any
  credits: {
    interview: number
    total: number
    breakdown: any
  }
}

interface UserState {
  profile: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
}

type UserAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PROFILE'; payload: UserProfile }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'CLEAR_USER' }
  | { type: 'UPDATE_PROFILE'; payload: Partial<UserProfile> }

const initialState: UserState = {
  profile: null,
  isLoading: false,
  isAuthenticated: false,
  error: null
}

let cachedProfileToken: string | null = null
let inFlightProfilePromise: Promise<void> | null = null

function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_PROFILE':
      return { 
        ...state, 
        profile: action.payload, 
        isLoading: false, 
        isAuthenticated: true,
        error: null 
      }
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false }
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload }
    case 'UPDATE_PROFILE':
      return {
        ...state,
        profile: state.profile ? { ...state.profile, ...action.payload } : null
      }
    case 'CLEAR_USER':
      return initialState
    default:
      return state
  }
}

interface UserContextType {
  state: UserState
  fetchUserProfile: () => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => void
  clearUser: () => void
  setAuthenticated: (isAuth: boolean) => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(userReducer, initialState)
  const fetchingRef = React.useRef(false)
  const lastTokenRef = React.useRef<string | null>(null)

  const fetchUserProfile = async () => {
    if (fetchingRef.current) return
    const token = localStorage.getItem('token')
    if (!token) {
      dispatch({ type: 'SET_AUTHENTICATED', payload: false })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }

    // Avoid repeat fetches when we already have a profile for the same token
    if (state.profile && lastTokenRef.current === token) {
      return
    }

    // If a fetch is already in progress for this token, reuse it
    if (inFlightProfilePromise && cachedProfileToken === token) {
      await inFlightProfilePromise
      return
    }

    fetchingRef.current = true
    cachedProfileToken = token
    inFlightProfilePromise = (async () => {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: null })

      const response = await fetch(buildApiUrl('users/profile/me'), {
        method: 'GET',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('token')
          dispatch({ type: 'SET_AUTHENTICATED', payload: false })
          dispatch({ type: 'SET_LOADING', payload: false })
          return
        }
        throw new Error(`Failed to fetch user profile: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success && data.data) {
        dispatch({ type: 'SET_PROFILE', payload: data.data })
        lastTokenRef.current = token
      } else {
        throw new Error(data.error || 'Failed to fetch user profile')
      }
    })().catch((error) => {
      console.error('Error fetching user profile:', error)
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to fetch user profile' 
      })
    }).finally(() => {
      fetchingRef.current = false
      inFlightProfilePromise = null
    })

    await inFlightProfilePromise
  }

  const updateProfile = (updates: Partial<UserProfile>) => {
    dispatch({ type: 'UPDATE_PROFILE', payload: updates })
  }

  const clearUser = () => {
    dispatch({ type: 'CLEAR_USER' })
  }

  const setAuthenticated = (isAuth: boolean) => {
    dispatch({ type: 'SET_AUTHENTICATED', payload: isAuth })
  }

  // Auto-fetch user profile on mount if token exists
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token && !state.profile && !state.isLoading) {
      setAuthenticated(true)
      fetchUserProfile()
    }
  }, [])

  // Listen for token changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token added/changed - fetch profile
          setAuthenticated(true)
          fetchUserProfile()
        } else {
          // Token removed - clear user
          clearUser()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const contextValue: UserContextType = {
    state,
    fetchUserProfile,
    updateProfile,
    clearUser,
    setAuthenticated
  }

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

// Helper hook for just the profile data
export function useUserProfile(): UserProfile | null {
  const { state } = useUser()
  return state.profile
}

// Helper hook for authentication status
export function useAuth() {
  const { state, setAuthenticated, clearUser } = useUser()
  
  const login = (token: string) => {
    localStorage.setItem('token', token)
    setAuthenticated(true)
  }

  const logout = () => {
    localStorage.removeItem('token')
    clearUser()
  }

  return {
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login,
    logout
  }
}
